// React port of Angular order-summary.component.ts. Visual cart whose
// items live in the page var named by `block.cartBinding`. Items carry
// denormalised display fields so this block renders without extra ERP
// fetches.

import { useEffect } from 'react'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { OrderSummaryBlock } from '@/pages/designers/page/types'
import { outputVarName } from '../bindingResolver'
import { productSelectionStore, useProductSelectionState } from '../productSelectionState'
import { injectEcomStyles } from './styles'

interface CartItem {
  cartItemId?: string
  productId: string
  productName?: string
  styleId: string
  styleName?: string
  imageUrl?: string
  customisationSummary?: string
  preferences?: Record<string, unknown>
  measurements?: Record<string, number>
  placementOverrides?: Record<string, unknown>
  qty: number
  unitPrice: number
  currency?: string
}

function initials(name?: string): string {
  if (!name) return '?'
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export default function OrderSummaryRenderer({ block }: { block: OrderSummaryBlock }) {
  injectEcomStyles()
  const runtime = useRuntime()
  const selState = useProductSelectionState()

  const cartVarName = outputVarName(block.cartBinding)
  const raw = cartVarName ? runtime.vars[cartVarName] : undefined
  const items: CartItem[] = Array.isArray(raw) ? (raw as CartItem[]) : []
  const currency = items[0]?.currency || 'INR'
  const subtotal = items.reduce(
    (sum, it) => sum + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0),
    0,
  )

  // Scroll-into-view best effort when an Edit kicks off elsewhere.
  useEffect(() => { /* no-op — kept symmetric with Angular OnInit */ }, [])

  function adjustQty(index: number, delta: number) {
    if (!cartVarName) return
    const copy = [...items]
    const item = copy[index]
    if (!item) return
    const next = Math.max(1, (Number(item.qty) || 1) + delta)
    if (next === item.qty) return
    copy[index] = { ...item, qty: next }
    runtime.setVar(cartVarName, copy)
  }

  function remove(index: number) {
    if (!cartVarName) return
    const copy = items.filter((_, i) => i !== index)
    runtime.setVar(cartVarName, copy)
  }

  function edit(item: CartItem) {
    if (!item.cartItemId) return
    productSelectionStore.beginEdit({
      cartItemId: item.cartItemId,
      productId: item.productId,
      styleId: item.styleId,
      qty: item.qty,
      unitPrice: item.unitPrice,
      preferences: item.preferences,
      measurements: item.measurements,
      placementOverrides: item.placementOverrides as Record<string, never> | undefined,
    })
    // Best-effort scroll to the product-selection block on the same page.
    if (typeof document !== 'undefined') {
      const el = document.querySelector('[data-ecom-block="product_selection"]') as HTMLElement | null
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  function isEditing(item: CartItem): boolean {
    const id = selState.editingCartItemId
    return !!id && item.cartItemId === id
  }

  const editable = block.editable !== false

  return (
    <section className="os" data-ecom-block="order_summary">
      <header className="os__head">
        <h3 className="os__title">Order summary</h3>
        {items.length > 0 && (
          <span className="os__count">{items.length} item{items.length === 1 ? '' : 's'}</span>
        )}
      </header>

      {items.length === 0 ? (
        <div className="os__empty">
          <div className="os__empty-icon">🛒</div>
          <div className="os__empty-title">{block.emptyStateTitle || 'No items yet'}</div>
          <div className="os__empty-body">{block.emptyStateBody || 'Pick a product to start the order.'}</div>
        </div>
      ) : (
        <>
          <ul className="os__list">
            {items.map((item, idx) => {
              const key = item.cartItemId || `${item.productId}|${item.styleId}|${idx}`
              const lineCurrency = item.currency || currency
              return (
                <li key={key} className="os__row">
                  {item.imageUrl ? (
                    <img className="os__thumb" src={item.imageUrl} alt={item.productName || ''} />
                  ) : (
                    <div className="os__thumb os__thumb--placeholder" aria-hidden="true">
                      {initials(item.productName)}
                    </div>
                  )}
                  <div className="os__rowmeta">
                    <div className="os__name">
                      {item.productName || item.productId}
                      <span className="os__sep" aria-hidden="true">·</span>
                      <span className="os__style">{item.styleName || item.styleId}</span>
                    </div>
                    {item.customisationSummary && (
                      <div className="os__detail">{item.customisationSummary}</div>
                    )}
                  </div>
                  <div className="os__qty">
                    {editable && (
                      <button type="button" className="os__qbtn"
                              onClick={() => adjustQty(idx, -1)}
                              disabled={item.qty <= 1}
                              aria-label="Decrease quantity">−</button>
                    )}
                    <span className="os__qval">{item.qty}</span>
                    {editable && (
                      <button type="button" className="os__qbtn"
                              onClick={() => adjustQty(idx, 1)}
                              aria-label="Increase quantity">+</button>
                    )}
                  </div>
                  <div className="os__line">
                    <div className="os__lineprice">{formatCurrency(item.unitPrice, lineCurrency)}</div>
                    <div className="os__linetotal">{formatCurrency(item.qty * item.unitPrice, lineCurrency)}</div>
                  </div>
                  {editable && (
                    <div className="os__actions">
                      <button
                        type="button"
                        className={`os__edit${isEditing(item) ? ' is-active' : ''}`}
                        onClick={() => edit(item)}
                        aria-label="Edit item"
                        title="Edit item"
                      >Edit</button>
                      <button type="button" className="os__remove"
                              onClick={() => remove(idx)}
                              aria-label="Remove item">×</button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>

          <footer className="os__foot">
            {block.showDiscount && (
              <div className="os__line-row"><span>Discount</span><span>—</span></div>
            )}
            {block.showTax && (
              <div className="os__line-row"><span>Tax</span><span>—</span></div>
            )}
            {block.showSubtotal !== false && (
              <div className="os__total-row">
                <span>Total</span>
                <strong>{formatCurrency(subtotal, currency)}</strong>
              </div>
            )}
          </footer>
        </>
      )}
    </section>
  )
}
